import { hasMixedCurrency } from './getCartCurrency';
/**
 * Returns the total price for all carts
 *
 * @param {Array} carts - list of cart objects
 * @return {Number} Total price
 */
export default function getCartTotal(carts=[]) {
	const mixedCurrency = hasMixedCurrency(carts);
	return carts.reduce((total, cart) => {
		let discountTotal = cart.products.reduce((dt, cp) => dt + cp.discount, 0);
		let productTotal = cart.products.reduce((pt, cp) => pt + cp.price, 0);
		let cartTotal = productTotal + (cart.taxPrice || 0) + (cart.shipPrice || 0) - discountTotal;
		// For mixed currency multicart orders, convert to USD
		if (mixedCurrency && cart?.store?.currencyCode !== "USD") {
			const inverseExchangeRate = (1 / cart.store.currencyExchangeRate);
			cartTotal = cartTotal * inverseExchangeRate;
		}
		return total + cartTotal;
	}, 0);
}

export function getCartSubtotal(carts=[]) {
	return carts.reduce((total, cart) => {
		let subtotal = cart.products.reduce((st, cp) => st + cp.price, 0);
		// TODO: See currency conversion in getCartTotal
		return total + subtotal;
	}, 0);
}

export function getCartTax(carts=[]) {
	return carts.reduce((total, cart) => {
		// TODO: See currency conversion in getCartTotal
		return total + (cart.taxPrice || 0);
	}, 0);
}

export function getCartShipping(carts=[]) {
	// NOTE: When shipPrice === null its pre-calculation
	// But dunno when to show '-.--' vs 'FREE' in multicart scenario
	return carts.reduce((total, cart) => {
		// TODO: See currency conversion in getCartTotal
		return total + (cart.shipPrice || 0);
	}, 0);
}

/**
 * Calculate per-delivery shipping breakdown
 *
 * @param {Array} carts - array or Cart objects
 * @return {Array} [shippingPerDelivery, ecomShipping]
 */
export function getCartShippingBreakdown(carts=[]) {
	const cartShipping = getCartShipping(carts);
	const cartDeliveries = getCartDeliveries(carts);

	// No breakdown necessary
	if (cartDeliveries === 1 || !cartShipping) {
		return [];
	}

	const ecomShipping = carts.reduce((total, cart) => {
		const ecoms = cart.products.filter(cp => !cp.subscriptionTypeTermId);
		// Get shipping cost of ecom products for this cart
		const cartEcomShipPrice = ecoms.reduce((ecomTotal, ecomCartProduct) => {
			return ecomTotal + (ecomCartProduct.shipping || 0);
		}, 0);
		// TODO: See currency conversion in getCartTotal
		// Add to all carts total
		return total + cartEcomShipPrice;
	}, 0);

	return [
		((cartShipping - ecomShipping) / cartDeliveries),
		ecomShipping,
	];
}

/**
 * Total number of subscription cycles purchased
 * Tally of all `numCycles` from carts
 *
 * @param {Array} carts - array or Cart objects
 * @return {Number} cartDeliveries
 */
export function getCartDeliveries(carts=[]) {
	return carts.reduce((deliveries, cart) => {
		return deliveries + cart.products.reduce((cnt, product) => {
			// wont count ecom products without cycles
			return cnt + (product?.subscriptionTypeTerm?.numCycles || 0);
		}, 0);
	}, 0);
}

export function getCartDiscount(carts=[]) {
	return carts.reduce((total, cart) => {
		let discountTotal = cart.products.reduce((dt, cp) => dt + cp.discount, 0);
		// TODO: See currency conversion in getCartTotal
		return total + discountTotal;
	}, 0);
}

export function getCartServiceFee(carts=[]) {
	const cartsTotal = getCartTotal(carts);
	const multicart = carts.length > 1;
	if (!multicart && !carts[0]?.store?.serviceFeeEnabled) {
		return 0;
	}

	// We force mixed currency to USD so exchange rate is 1
	const currencyExchangeRate = hasMixedCurrency(carts) ? 1 : carts[0].store.currencyExchangeRate;
	return Math.max(
		parseInt(Math.round(window.cj.service_fee_min_cents * currencyExchangeRate)),
		parseInt(Math.round(cartsTotal * window.cj.service_fee_rate))
	);
}
